import React, { useContext, useState, useEffect } from 'react';
import { UserContext } from '../../context/UserContext';

function ProductCategoryManager({ shopSubdomain, defaultCategory }) {
  const [categories, setCategories] = useState([]);
  const [currentCategoryId, setCurrentCategoryId] = useState(defaultCategory || 'all');
  const [currentSubcategoryId, setCurrentSubcategoryId] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [token, , , venueID] = useContext(UserContext);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const requestOptions = {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        };
        const response = await fetch(`/v1/get-categories-with-subcategories`, requestOptions);
        if (!response.ok) throw new Error('Failed to fetch categories');
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    }
    fetchCategories();
  }, [token, shopSubdomain]);

  useEffect(() => {
    async function fetchProducts() {
      if (!token || !venueID) return;
      try {
        const requestOptions = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
            "x-venue-id": venueID,
          },
        };
        const response = await fetch(`/v1/get-venue-products`, requestOptions);
        if (!response.ok) throw new Error('Failed to fetch products');
        const data = await response.json();
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    }
    fetchProducts();
  }, [token, venueID]);

  useEffect(() => {
    const filtered = currentSubcategoryId
      ? products.filter(product => product.subcategory_id === currentSubcategoryId)
      : currentCategoryId === 'all'
        ? products
        : products.filter(product => product.category_id === currentCategoryId);
    setFilteredProducts(filtered);
  }, [currentCategoryId, currentSubcategoryId, products]);

  const handleDelete = async (id) => {
    const requestOptions = {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    };
    
    try {
      const response = await fetch(`/v1/products/${id}`, requestOptions);
      if (!response.ok) throw await response.json();
      window.location.reload();
    } catch (error) {
      console.error(error.detail || 'An error occurred during registration.');
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!venueID) {
      console.error('No venue ID available');
      return;
    }
    try {
      const response = await fetch(`/v1/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: "Bearer " + token,
          'X-Venue-ID': venueID,
        },
        body: JSON.stringify({ name: newCategoryName }),
      });
      if (!response.ok) throw new Error('Failed to create category');
      const data = await response.json();
      setCategories([...categories, data]);
      setNewCategoryName('');
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const handleCreateSubcategory = async (e) => {
    e.preventDefault();
    
    // Creating subcategory
    
    try {
        const response = await fetch(`/categories/${selectedCategoryId}/subcategories`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: "Bearer " + token,
            },
            body: JSON.stringify({ 
                name: newSubcategoryName, 
                category_id: selectedCategoryId  // Include category_id in the request body
            }),
        });
        
        if (!response.ok) throw new Error('Failed to create subcategory');
        const data = await response.json();
        
        setCategories(categories.map(category => 
            category.id === selectedCategoryId 
                ? { ...category, subcategories: [...category.subcategories, data] }
                : category
        ));
        
        setNewSubcategoryName('');
        setSelectedCategoryId('');
    } catch (error) {
        console.error('Error creating subcategory:', error);
    }
};


  return (
    <>
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200 flex-auto">
        <form onSubmit={handleCreateCategory}>
          <div className='flex flex-col gap-4'>
            <div className="bg-white h-16 px-4 flex items-center border-b border-gray-200 justify-between rounded">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="New Category Name"
                className="text-sm focus:outline-none active:outline-none border border-gray-300 w-[45rem] h-10 pl-11 pr-4 rounded"
              />
              <button type="submit" 
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded shadow-md"
              >Create Categorydd
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="bg-white p-4 rounded-lg shadow border border-gray-200 flex-auto">
        <form onSubmit={handleCreateSubcategory}>
          <div className='flex flex-col gap-4'>
            <div className="bg-white h-16 px-4 flex items-center border-b border-gray-200 justify-between rounded">
              <input
                type="text"
                value={newSubcategoryName}
                onChange={(e) => setNewSubcategoryName(e.target.value)}
                placeholder="New Subcategory Name"
                className="text-sm focus:outline-none active:outline-none border border-gray-300 w-[20rem] h-10 pl-11 pr-4 rounded"
              />
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                className="text-sm focus:outline-none active:outline-none border border-gray-300 w-[20rem] h-10 pl-11 pr-4 rounded"
              >
                <option value="" disabled>Select Category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
              <button type="submit" 
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded shadow-md"
              >Create Subcategory
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="bg-white p-4 rounded-lg shadow border border-gray-200 flex-auto">
        <div className="flex flex-wrap mb-4">
          <button
            onClick={() => {
              setCurrentCategoryId('all');
              setCurrentSubcategoryId(null);
            }}
            className={`p-2 m-1 ${currentCategoryId === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            All Products
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => {
                setCurrentCategoryId(category.id);
                setCurrentSubcategoryId(null);
              }}
              className={`p-2 m-1 ${currentCategoryId === category.id ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
            >
              {category.name}
            </button>
          ))}
        </div>
        
        <div className="flex flex-wrap mb-4">
          {categories
            .filter(category => category.id === currentCategoryId && category.subcategories.length > 0)
            .flatMap(category => category.subcategories)
            .map((subcategory) => (
              <button
                key={subcategory.id}
                onClick={() => setCurrentSubcategoryId(subcategory.id)}
                className={`p-2 m-1 ${currentSubcategoryId === subcategory.id ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
              >
                {subcategory.name}
              </button>
          ))}
        </div>
  
        <div className="grid grid-cols-4 gap-4">
          {filteredProducts.map((product) => (
            <div key={product.id} 
              className="p-4 border border-gray-200 rounded-lg">
              {product.image_path }
              <img 
                //src ='/static/kong/images/cola_real.webp/51aabe9b-3529-44ed-a422-72c0a93b3521.webp'
                src={product.image_path} // Use the image path from the product, or a placeholder if none
                alt={product.name} 
                className=" mx-auto rounded-lg" 
              />
              <h3 className="text-lg font-bold">{product.name}</h3>
              <p>Id: {product.id}</p>
              <p>Precio: €{product.price}</p>
              <p>Stock: {product.stock_quantity}</p>
              <td className="py-4 px-3 flex justify-between">
                <button
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-l"
                  // onClick={() => handleEdit(user.id)} // Uncomment and modify as needed
                >
                  Edit
                </button>
                <button
                  className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-r"
                  onClick={(event) => handleDelete(product.id)}
                >
                  Delete
                </button>
              </td>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ProductCategoryManager;
